<template>
  <el-card class="order-log-filters">
    <el-form :model="form" label-position="top" @submit.native.prevent="submit">
      <el-row :gutter="20">
        <el-col :lg="6" :md="12" :sm="12">
          <el-form-item>
            <template slot="label">
              <el-select v-model="form.numberType" :aria-label="$t('orderLogAddress.numberType')" style="width: 100%">
                <el-option :label="$t('newOrder.waybillNumber') + '/' + $t('GlobalSetObj.serviceLogistics')" :value="numberTypes.WAYBILL" />
                <el-option :label="$t('orderCenterCont.TheOrderNumberCustomer')" :value="numberTypes.ORDER" />
                <el-option :label="$t('placeOrder.customerNumber')" :value="numberTypes.CUSTOMER" />
              </el-select>
            </template>
            <div class="number-search">
              <el-input
                v-model.trim="form.number" :placeholder="$t('orderDetails.pleaseInput')" clearable
                @keyup.enter.native="singleSearch"
              />
              <el-button icon="el-icon-search" :aria-label="$t('operationCenter.Procurar')" @click="singleSearch" />
              <el-popover
                v-model="showNumbers" placement="bottom-end" trigger="manual"
                width="330"
              >
                <el-input
                  v-model="batchText" type="textarea" :rows="10"
                  :placeholder="$t('newOrder.PreciseSearch')"
                />
                <div class="batch-actions">
                  <el-button size="small" @click="clearNumbers">{{ $t('newOrder.empty') }}</el-button>
                  <el-button size="small" @click="showNumbers = false">{{ $t('orderExport.close') }}</el-button>
                  <el-button size="small" type="primary" @click="batchSearch">{{ $t('newOrder.search') }}</el-button>
                </div>
                <el-button
                  slot="reference" icon="el-icon-document" :aria-label="$t('newOrder.PreciseSearch')"
                  @click="showNumbers = !showNumbers"
                />
              </el-popover>
            </div>
          </el-form-item>
        </el-col>
        <el-col :lg="8" :md="12" :sm="12">
          <el-form-item :label="$t('GlobalSetObj.CreationTime') + ' (' + $t('orderLogAddress.brazilTime') + ')'">
            <el-date-picker
              v-model="form.dates" type="datetimerange" value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']" :range-separator="$t('operationCenter.to')"
              :start-placeholder="$t('operationCenter.startDate')" :end-placeholder="$t('operationCenter.endDate')"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
        <el-col :lg="6" :md="12" :sm="12">
          <el-form-item :label="$t('receivablePayable.customer')">
            <el-select
              v-model="form.customerId" :placeholder="$t('spareNumberPoo.Only10AreDisplayed')"
              :remote-method="getCustomers" clearable filterable
              remote
              reserve-keyword style="width: 100%" @focus="getCustomers()"
            >
              <el-option
                v-for="customer in customers" :key="customer.id" :label="customer.customerAlias + '(' + customer.username + ')'"
                :value="customer.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :lg="4" :md="12" :sm="12">
          <el-form-item :label="$t('orderCenterCont.WhetherSuccessful')">
            <el-select v-model="form.state" style="width: 100%">
              <el-option :label="$t('GlobalSetObj.all')" :value="null" />
              <el-option :label="$t('GlobalSetObj.yes')" :value="states.SUCCESS" />
              <el-option :label="$t('GlobalSetObj.No')" :value="states.FAILURE" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :lg="6" :md="12" :sm="12">
          <el-form-item :label="$t('orderLogAddress.result')">
            <el-select v-model="form.receiveAddressCheckResult" style="width: 100%">
              <el-option :label="$t('GlobalSetObj.all')" :value="null" />
              <el-option
                v-for="value in results" :key="value" :value="value"
                :label="$t('orderLogAddress.' + value)"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <div v-if="batchNumbers.length" class="batch-summary">
        <el-tooltip placement="bottom"><pre slot="content">{{ batchText }}</pre><span>{{ batchNumbers.join('，') }}</span></el-tooltip>
        <el-button
          type="text" icon="el-icon-close" :aria-label="$t('newOrder.empty')"
          @click="clearNumbers"
        />
      </div>
      <el-button
        icon="el-icon-search" size="small" type="primary"
        @click="submit"
      >{{ $t('operationCenter.Procurar') }}</el-button>
      <el-button icon="el-icon-refresh-left" size="small" @click="reset">{{ $t('operationCenter.reset') }}</el-button>
      <el-button size="small" :disabled="exportDisabled" @click="$emit('export')">{{ $t('operationCenter.exports') }}</el-button>
      <p class="query-hint"><i class="el-icon-info" /> {{ $t('orderLogAddress.hint') }}</p>
    </el-form>
  </el-card>
</template>
<script>
import { apiCustomerPage } from '@/api/customer';
import {
  ADDRESS_CHECK_RESULTS, NUMBER_TYPE, ORDER_LOG_STATE, buildOrderLogParams,
  defaultOrderLogDates, validateOrderLogDates
} from './orderLogQuery';
const initialForm = () => ({
  number: '', numberType: NUMBER_TYPE.WAYBILL, customerId: '', state: null,
  receiveAddressCheckResult: null, dates: defaultOrderLogDates()
});
export default {
  name: 'OrderLogFilters',
  props: { exportDisabled: { type: Boolean, default: true }},
  data() {
    return {
      form: initialForm(), numberTypes: NUMBER_TYPE, states: ORDER_LOG_STATE,
      results: Object.values(ADDRESS_CHECK_RESULTS), customers: [],
      showNumbers: false, batchText: '', batchNumbers: []
    };
  },
  mounted() { this.submit(); },
  methods: {
    submit() {
      const error = validateOrderLogDates(this.form.dates);
      if (error) return this.$message.warning(this.$t('orderLogAddress.' + error));
      const params = buildOrderLogParams(this.form, this.batchNumbers);
      if (Math.max(params.orderNumberList.length, params.logisticsNumberList.length, params.customerNumberList.length) > 200) {
        return this.$message.warning(this.$t('orderLogAddress.maxNumbers'));
      }
      this.$emit('query', params);
    },
    reset() { this.form = initialForm(); this.clearNumbers(); this.showNumbers = false; this.submit(); },
    clearNumbers() { this.batchText = ''; this.batchNumbers = []; },
    singleSearch() { this.clearNumbers(); this.submit(); },
    batchSearch() {
      this.batchNumbers = [...new Set(this.batchText.split('\n').map((number) => number.trim()).filter(Boolean))];
      this.form.number = ''; this.showNumbers = false; this.submit();
    },
    async getCustomers(name = '') {
      try {
        const result = await apiCustomerPage({ pageSize: 10, pageNumber: 1, params: { quickSearch: name }});
        if (result.code === 200) this.customers = result.data.records;
      } catch (error) {
        this.$message.error(error.message || this.$t('orderLogAddress.queryFailed'));
      }
    }
  }
};
</script>
<style scoped>
.order-log-filters { margin-bottom: 20px; }
.order-log-filters .el-row { display: flex; flex-wrap: wrap; align-items: flex-end; }
.order-log-filters >>> .el-form-item__label { width: 100%; }
.number-search { display: flex; }
.number-search >>> .el-input__inner { border-top-right-radius: 0; border-bottom-right-radius: 0; }
.number-search > .el-button { margin-left: -1px; border-radius: 0; padding-inline: 10px; }
.number-search .el-popover__reference { padding-inline: 10px; }
.batch-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 10px; }
.query-hint { margin: 16px 0 0; font-size: 13px; color: #657180; line-height: 1.6; }
.batch-summary { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; color: #007c3e; }
.batch-summary span { max-width: 90%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
